/* eslint-disable max-len */
/* eslint-disable no-alert */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'supports/api';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Modal,
} from 'reactstrap';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import qs from 'query-string';
import { API_URL_1 } from '../../supports/api-url/apiurl';
import ScheduleCard from './ScheduleCard';
import ScheduleForm from './ScheduleForm';
import ProgramHeaderForm from './ProgramHeaderForm';
import ProgramDetailFilters from './ProgramDetailFilters';
import useFilteredPrograms from './hooks/useFilteredPrograms';

const defaultShownProgramsCount = 25;

const defaultProgramFilters = {
  code: '',
  branchIds: [],
  startDateMonth: null,
  startTime: null,
  studyMethodIds: [],
  studyScheduleIds: [],
};

const CustomTab = ({ tabId, activeTab, onClick, title }) => (
  <NavItem>
    <NavLink
      active={tabId === activeTab}
      onClick={() => onClick(tabId)}
      className="rounded-0"
    >
      {title}
    </NavLink>
  </NavItem>
);

const ProgramDetailsCard = ({
  image,
  name,
  category,
  onEdit,
  editHeaderModal,
  toggleEditHeaderModal,
  programHeaderId,
  refresh,
}) => (
  <Card className="my-1" style={{ height: '220px' }}>
    <CardHeader className="font-weight-bold">
      Program Details{' '}
      <Button className="float-right" size="sm" onClick={onEdit}>
        Edit
      </Button>
    </CardHeader>
    <CardBody>
      <div className="row">
        <div className="col-12 font-sm-10 font-md-12">
          <div className="d-inline-flex flex-row flex-sm-column flex-md-row">
            <div className="d-flex flex-row py-1">
              <img
                src={API_URL_1 + image}
                alt={image || 'noimage'}
                style={{ maxHeight: 100 }}
                className="pr-3"
              />
            </div>
            <div className="d-flex flex-column flex-sm-column flex-md-row py-1">
              <div className="pr-5">
                <div className="font-weight-bold text-gray">Category</div>
                <div className="pb-1">{category}</div>
                <div className="font-weight-bold text-gray">Name</div>
                <div className="pb-1">{name}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardBody>
    <Modal isOpen={editHeaderModal} toggle={toggleEditHeaderModal} size="lg">
      <ProgramHeaderForm
        edit
        programHeaderId={programHeaderId}
        toggle={toggleEditHeaderModal}
        refresh={refresh}
      />
    </Modal>
  </Card>
);

const ProgramDescriptionCard = ({ description }) => (
  <Card className="my-1" style={{ height: '220px' }}>
    <CardHeader className="font-weight-bold">Description</CardHeader>
    <CardBody>
      <div className="row">
        <div className="col-12 font-sm-10 font-md-12">{description}</div>
      </div>
    </CardBody>
  </Card>
);

const ProgramDetails = () => {
  const [programHeader, setProgramHeader] = useState({});
  const [programs, setPrograms] = useState([]);

  const [addModal, setAddModal] = useState(false);
  const [editHeaderModal, setEditHeaderModal] = useState(false);

  const [activeTab, setActiveTab] = useState('Live');
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [programFilters, setProgramFilters] = useState(defaultProgramFilters);

  const location = useLocation();
  const history = useHistory();
  const { id } = useParams();

  const getPrograms = useCallback(() => {
    axios
      .get(`/admin/program/header/${id}`)
      .then(({ data }) => {
        if (!data.result) {
          alert('Invalid id');
          history.push('');
        } else {
          setProgramHeader(data.result);
          setPrograms(data.result.programs || []);
        }
      })
      .catch((err) => {
        alert(err.message);
      });
  }, [id, history]);

  useEffect(() => {
    getPrograms();
  }, [getPrograms]);

  const onNavClick = (tabId) => setActiveTab(tabId);
  const onEditProgramHeader = () => setEditHeaderModal((prev) => !prev);
  const onAddClick = () => setAddModal((prev) => !prev);

  const params = new URLSearchParams(location.search);
  const programHeaderId = params.get('id');

  const { filteredPrograms, totalPrograms } = useFilteredPrograms({
    programs,
    activeTab,
    showAllPrograms,
    programFilters,
  });

  const onBack = () => {
    history.push('/admin/programs?'.concat(qs.stringify(location.state)));
  };

  return (
    <div className="white-box py-3 container">
      <div className="row">
        <div className="col-12">
          <div>
            <div className="line-green mb-4" />
            <div className="general-title text-gray">
              Program Details{' '}
              <Button className="float-right" onClick={onBack}>
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="row py-3">
        <div className="col-12">
          <div className="row">
            <div className="col-12 col-sm">
              <ProgramDetailsCard
                {...programHeader}
                onEdit={onEditProgramHeader}
                editHeaderModal={editHeaderModal}
                toggleEditHeaderModal={onEditProgramHeader}
                programHeaderId={programHeaderId}
                refresh={getPrograms}
              />
            </div>
            <div className="col-12 col-sm">
              <ProgramDescriptionCard {...programHeader} />
            </div>
          </div>
          <Card>
            <CardHeader className="font-weight-bold">
              <div className="d-flex flex-row justify-content-between">
                <div className="my-auto">Schedules</div>
                <div>
                  <Button color="success" onClick={onAddClick} size="sm">
                    +Add
                  </Button>
                </div>
              </div>
            </CardHeader>
            <div>
              <Nav pills fill justified className="border-left border-right">
                <CustomTab
                  tabId="Not Live"
                  activeTab={activeTab}
                  title="Not Live"
                  onClick={onNavClick}
                />
                <CustomTab
                  tabId="Live"
                  activeTab={activeTab}
                  title="Live"
                  onClick={onNavClick}
                />
                <CustomTab
                  tabId="Past"
                  activeTab={activeTab}
                  title="Past"
                  onClick={onNavClick}
                />
              </Nav>

              <ProgramDetailFilters
                programFilters={programFilters}
                setProgramFilters={setProgramFilters}
                defaultProgramFilters={defaultProgramFilters}
              />

              <div className="border-left border-bottom border-right border-top">
                {filteredPrograms.map((program) => (
                  <ScheduleCard
                    {...program}
                    refresh={getPrograms}
                    key={program.id}
                    programCategoryId={programHeader.categoryId}
                    programCategoryType={programHeader.categoryType}
                    isMultipleCurrency={programHeader.isMultipleCurrency}
                  />
                ))}
              </div>
              {!showAllPrograms && totalPrograms > defaultShownProgramsCount && (
                <div className="d-flex justify-content-center">
                  <Button
                    className="rounded ml-1 my-1"
                    onClick={() => setShowAllPrograms(true)}
                  >
                    Show All
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
      <Modal isOpen={addModal} size="lg" toggle={onAddClick}>
        <ScheduleForm
          programCategoryId={programHeader.categoryId}
          programCategoryType={programHeader.categoryType}
          toggle={onAddClick}
          refresh={getPrograms}
          isMultipleCurrency={programHeader.isMultipleCurrency}
        />
      </Modal>
    </div>
  );
};

export default ProgramDetails;
